"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Timer } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { CountdownStyle, ResolvedPromoCountdown } from "../types";
import { getLinkProps } from "../partials/promoHelpers";
import { CtaButton } from "../partials/CtaButton";
import { resolveCountdownView, type TimeParts } from "./countdownState";
import { useCountdown } from "./useCountdown";
export interface PromoCountdownProps {
  countdown: ResolvedPromoCountdown;
  textColor: string;
  ctaBackground: string;
  ctaTextColor: string;
  /** False inside a whole-card link, where the expired CTA must not be a nested link */
  interactive: boolean;
  align?: "start" | "center";
}
const ZERO: TimeParts = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  expired: true
};
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Countdown block rendered below a promo's description. Only this block reacts
 * to expiry — the surrounding promo never changes.
 */
export function PromoCountdown(t0) {
  const $ = _c(28);
  const {
    countdown,
    textColor,
    ctaBackground,
    ctaTextColor,
    interactive,
    align: t1
  } = t0;
  const align = t1 === undefined ? "start" : t1;
  const parts = useCountdown(countdown.targetDate, countdown.endedAtRender);
  const nav = useNavigation();
  const labels = useLabels().promoCountdown;
  const centered = align === "center" || countdown.style === "large";
  if (!parts) {
    let t2;
    if ($[0] !== centered || $[1] !== countdown.label || $[2] !== countdown.style || $[3] !== labels) {
      t2 = <div aria-hidden={true}><Digits style={countdown.style} label={countdown.label} parts={null} labels={labels} centered={centered} /></div>;
      $[0] = centered;
      $[1] = countdown.label;
      $[2] = countdown.style;
      $[3] = labels;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  let view;
  if ($[5] !== centered || $[6] !== countdown || $[7] !== ctaBackground || $[8] !== ctaTextColor || $[9] !== interactive || $[10] !== nav || $[11] !== parts || $[12] !== textColor) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      view = resolveCountdownView(countdown, parts);
      if (view.kind === "hidden") {
        t2 = null;
        break bb0;
      }
      if (view.kind === "replace") {
        const {
          href,
          isExternal
        } = getLinkProps(view.ctaLink, nav.basePath);
        const showCta = Boolean(view.ctaText) && (!interactive || Boolean(href));
        if (!view.message && !showCta) {
          t2 = null;
          break bb0;
        }
        const t3 = `flex flex-col gap-3 ${centered ? "items-center text-center" : "items-start"}`;
        let t4;
        if ($[15] !== textColor) {
          t4 = {
            color: textColor
          };
          $[15] = textColor;
          $[16] = t4;
        } else {
          t4 = $[16];
        }
        const t5 = view.message && <p className="text-base font-bold">{view.message}</p>;
        const t6 = showCta && (interactive && href ? <CtaButton href={href} isExternal={isExternal} text={view.ctaText ?? ""} backgroundColor={ctaBackground} textColor={ctaTextColor} /> : <span className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-full font-bold text-sm shadow-sm whitespace-nowrap text-center" style={{
          backgroundColor: ctaBackground,
          color: ctaTextColor
        }}>{view.ctaText}</span>);
        let t7;
        if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
          t7 = <div className={t3} style={t4}>{t5}{t6}</div>;
          $[17] = t3;
          $[18] = t4;
          $[19] = t5;
          $[20] = t6;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        t2 = t7;
        break bb0;
      }
    }
    $[5] = centered;
    $[6] = countdown;
    $[7] = ctaBackground;
    $[8] = ctaTextColor;
    $[9] = interactive;
    $[10] = nav;
    $[11] = parts;
    $[12] = textColor;
    $[13] = t2;
    $[14] = view;
  } else {
    t2 = $[13];
    view = $[14];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const t3 = view.kind === "running" ? view.parts : ZERO;
  let t4;
  if ($[22] !== centered || $[23] !== countdown.label || $[24] !== countdown.style || $[25] !== labels || $[26] !== t3) {
    t4 = <div role="timer"><Digits style={countdown.style} label={countdown.label} parts={t3} labels={labels} centered={centered} /></div>;
    $[22] = centered;
    $[23] = countdown.label;
    $[24] = countdown.style;
    $[25] = labels;
    $[26] = t3;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  return t4;
}
interface DigitsProps {
  style: CountdownStyle;
  label: string | null;
  /** null renders placeholders ("--") with the same layout */
  parts: TimeParts | null;
  labels: {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
    daysShort: string;
  };
  centered: boolean;
}
function Digits(t0) {
  const $ = _c(64);
  const {
    style,
    label,
    parts,
    labels,
    centered
  } = t0;
  let t1;
  if ($[0] !== parts) {
    t1 = n => parts && n !== undefined ? pad2(n) : "--";
    $[0] = parts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  const t2 = parts?.days;
  let t3;
  if ($[2] !== t2 || $[3] !== value) {
    t3 = value(t2);
    $[2] = t2;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== labels.days || $[6] !== t3) {
    t4 = {
      value: t3,
      label: labels.days
    };
    $[5] = labels.days;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = parts?.hours;
  let t6;
  if ($[8] !== t5 || $[9] !== value) {
    t6 = value(t5);
    $[8] = t5;
    $[9] = value;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== labels.hours || $[12] !== t6) {
    t7 = {
      value: t6,
      label: labels.hours
    };
    $[11] = labels.hours;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = parts?.minutes;
  let t9;
  if ($[14] !== t8 || $[15] !== value) {
    t9 = value(t8);
    $[14] = t8;
    $[15] = value;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== labels.minutes || $[18] !== t9) {
    t10 = {
      value: t9,
      label: labels.minutes
    };
    $[17] = labels.minutes;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = parts?.seconds;
  let t12;
  if ($[20] !== t11 || $[21] !== value) {
    t12 = value(t11);
    $[20] = t11;
    $[21] = value;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== labels.seconds || $[24] !== t12) {
    t13 = {
      value: t12,
      label: labels.seconds
    };
    $[23] = labels.seconds;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t10 || $[27] !== t13 || $[28] !== t4 || $[29] !== t7) {
    t14 = [t4, t7, t10, t13];
    $[26] = t10;
    $[27] = t13;
    $[28] = t4;
    $[29] = t7;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const units = t14;
  if (style === "inline") {
    const days = parts && parts.days > 0 ? `${parts.days}${labels.daysShort} ` : "";
    const clock = `${units[1].value}:${units[2].value}:${units[3].value}`;
    const t15 = `inline-flex flex-wrap items-center gap-1.5 rounded-full bg-black/25 px-3 py-1 text-xs font-bold tabular-nums ${centered ? "justify-center" : ""}`;
    let t16;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <Timer className="h-3.5 w-3.5 shrink-0" aria-hidden={true} />;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] !== label) {
      t17 = label && <span>{label}</span>;
      $[32] = label;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    let t18;
    if ($[34] !== clock || $[35] !== days) {
      t18 = <span className="whitespace-nowrap">{days}{clock}</span>;
      $[34] = clock;
      $[35] = days;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    let t19;
    if ($[37] !== t15 || $[38] !== t17 || $[39] !== t18) {
      t19 = <div className={t15}>{t16}{t17}{t18}</div>;
      $[37] = t15;
      $[38] = t17;
      $[39] = t18;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    return t19;
  }
  if (style === "compact") {
    const t15 = `flex flex-wrap items-center gap-2 ${centered ? "justify-center" : ""}`;
    let t16;
    if ($[41] !== label) {
      t16 = label && <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</span>;
      $[41] = label;
      $[42] = t16;
    } else {
      t16 = $[42];
    }
    let t17;
    if ($[43] !== units) {
      t17 = <div className="flex gap-1.5 tabular-nums">{units.map(_temp)}</div>;
      $[43] = units;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== t15 || $[46] !== t16 || $[47] !== t17) {
      t18 = <div className={t15}>{t16}{t17}</div>;
      $[45] = t15;
      $[46] = t16;
      $[47] = t17;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    return t18;
  }
  if (style === "large") {
    let t15;
    if ($[49] !== label) {
      t15 = label && <div className="mb-2 text-xs uppercase tracking-widest opacity-80">{label}</div>;
      $[49] = label;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    let t16;
    if ($[51] !== units) {
      t16 = <div className="flex items-end justify-center gap-2 sm:gap-4 tabular-nums font-extrabold">{units.map(_temp2)}</div>;
      $[51] = units;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    let t17;
    if ($[53] !== t15 || $[54] !== t16) {
      t17 = <div className="flex flex-col items-center text-center">{t15}{t16}</div>;
      $[53] = t15;
      $[54] = t16;
      $[55] = t17;
    } else {
      t17 = $[55];
    }
    return t17;
  }
  const t15 = `flex flex-col ${centered ? "items-center text-center" : "items-start"}`;
  let t16;
  if ($[56] !== label) {
    t16 = label && <div className="mb-2 text-xs uppercase tracking-widest opacity-80">{label}</div>;
    $[56] = label;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== units) {
    t17 = <div className="flex gap-2 tabular-nums">{units.map(_temp3)}</div>;
    $[58] = units;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== t15 || $[61] !== t16 || $[62] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[60] = t15;
    $[61] = t16;
    $[62] = t17;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  return t18;
}
function _temp3(u_1, i_1) {
  return <div key={i_1} className="flex min-w-14 flex-col items-center rounded-lg bg-black/30 px-3 py-2 backdrop-blur-sm sm:min-w-16"><div className="text-2xl font-bold leading-none sm:text-3xl">{u_1.value}</div><div className="mt-1.5 text-[10px] uppercase tracking-wide opacity-75">{u_1.label}</div></div>;
}
function _temp2(u_0, i_0) {
  return <React.Fragment key={i_0}>{i_0 > 0 && <div className="pb-5 text-3xl opacity-40 sm:pb-6 sm:text-5xl">:</div>}<div className="flex flex-col items-center"><div className="text-4xl leading-none sm:text-6xl">{u_0.value}</div><div className="mt-1 text-[10px] font-normal uppercase tracking-widest opacity-70 sm:text-xs">{u_0.label}</div></div></React.Fragment>;
}
function _temp(u, i) {
  return <div key={i} className="min-w-10 rounded-md bg-white/20 px-2 py-1 text-center"><div className="text-base font-extrabold leading-none">{u.value}</div><div className="mt-0.5 text-[9px] uppercase tracking-wide opacity-75">{u.label}</div></div>;
}